/**
 * MethodologyDisclaimer — audit-remediation Phase 1.
 *
 * Renders a compact, clearly-branded methodology / disclaimer card for
 * FoodPulse pages that make nutrition, allergen, food-safety or Label
 * Scanner claims. The audit specifically requires visible source-status
 * information near the claim — this component surfaces last-reviewed
 * dates, data provenance, and a "we can be wrong" nudge without stealing
 * the page's hero. Slot it directly under any AI-assisted output.
 *
 * Usage:
 *   <MethodologyDisclaimer
 *     kind="nutrition"
 *     lastReviewed="2026-02-25"
 *     sources={["USDA FoodData Central", "Manufacturer label"]}
 *   />
 */
import { Info, BookOpen, ShieldAlert } from "lucide-react";

const KIND_COPY = {
  nutrition: {
    title: "How we calculate nutrition",
    body: "Values are drawn from the label, USDA FoodData Central, and community contributions. Packaging remains the authoritative source — formulations change.",
    icon: BookOpen,
  },
  allergen: {
    title: "About allergen data",
    body: "AI-generated. Cross-contact, formulation changes and packaging variants may not be reflected. Always read the manufacturer label if you have an allergy.",
    icon: ShieldAlert,
  },
  scan: {
    title: "How the Label Scanner works",
    body: "The scanner OCRs the label, matches ingredients to the FoodPulse encyclopedia, and highlights known additives. Coverage is growing — please correct any mismatch you find.",
    icon: Info,
  },
  ai: {
    title: "AI-assisted content",
    body: "This page includes text generated with an AI assistant and reviewed by our editors. Corrections are welcome via the report link.",
    icon: Info,
  },
  ingredient: {
    title: "About ingredient interpretations",
    body: "Descriptions summarise widely-available food-science and regulatory data. They are not medical advice. Please consult a professional for personal dietary questions.",
    icon: BookOpen,
  },
  medical: {
    title: "Not medical advice",
    body: "Health notes are informational and are not a substitute for professional medical care. Emergency? Call your local emergency number.",
    icon: ShieldAlert,
  },
};

export default function MethodologyDisclaimer({
  kind = "nutrition",
  lastReviewed,
  sources = [],
  className = "",
  compact = false,
  testid,
}) {
  const copy = KIND_COPY[kind] || KIND_COPY.nutrition;
  const Icon = copy.icon;
  return (
    <aside
      className={`rounded-xl border border-amber-200 bg-amber-50/70 p-3 sm:p-4 text-[12px] leading-snug text-amber-950 ${className}`}
      data-testid={testid || `methodology-${kind}`}
      role="note"
      aria-label={copy.title}
    >
      <div className="flex items-start gap-2">
        <Icon className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-amber-900">{copy.title}</div>
          {!compact && <p className="mt-0.5 text-amber-900/80">{copy.body}</p>}
          {(sources?.length > 0 || lastReviewed) && (
            <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-amber-800/80">
              {sources?.length > 0 && (
                <span>
                  <b>Sources:</b> {sources.join(", ")}
                </span>
              )}
              {lastReviewed && (
                <span>
                  <b>Last reviewed:</b> {lastReviewed}
                </span>
              )}
              <a
                href="/food-pulse/methodology"
                className="underline hover:text-amber-900"
                data-testid={`${testid || `methodology-${kind}`}-link`}
              >
                Read our methodology →
              </a>
            </div>
          )}
        </div>
      </div>
    </aside>
  );
}
